import { Link, useParams } from "@tanstack/react-router";
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pause, Play, Square } from "lucide-react";
import { toast } from "sonner";
import { cancelRun, getRun, pauseRun, resumeRun, runSnapshots } from "@/api/runs";
import { Page } from "@/components/page";
import { OutcomeBar } from "@/components/outcome-bar";
import { PauseErrorBanner } from "@/components/pause-error-banner";
import { ProgressChart } from "@/components/progress-chart";
import { RunErrors } from "@/components/run-errors";
import { RunHero } from "@/components/run-hero";
import { RunSankey } from "@/components/run-sankey";
import { RunTempo } from "@/components/run-tempo";
import { useRunFromList, useRunSnapshots } from "@/hooks/useEventStream";

export function RunDetailPage() {
  const { runId } = useParams({ strict: false }) as { runId: string };
  const qc = useQueryClient();

  // One-shot fetch for the cold paint and for terminal runs that may
  // already have aged off the recent-runs list. RUNNING runs always sit
  // in the SSE stream's `runs.recent_runs` while running, so the live
  // override below takes over within ~1 s of the first progress event.
  const run = useQuery({
    queryKey: ["run", runId],
    queryFn: () => getRun(runId),
  });
  const live = useRunFromList(runId);

  // Snapshot history feeds the hero ETA, the tempo cards, and the
  // existing chart. One-shot historical backfill + SSE-pushed live tail.
  const histSnaps = useQuery({
    queryKey: ["snapshots", runId],
    queryFn: () => runSnapshots(runId),
  });
  const liveSnaps = useRunSnapshots(runId);
  const snapshots = useMemo(() => {
    const seen = new Set<string>();
    const merged = [...(histSnaps.data ?? []), ...liveSnaps];
    const deduped = merged.filter((s) => {
      if (seen.has(s.at)) return false;
      seen.add(s.at);
      return true;
    });
    deduped.sort((a, b) => a.at.localeCompare(b.at));
    return deduped;
  }, [histSnaps.data, liveSnaps]);

  const invalidateRuns = () => {
    qc.invalidateQueries({ queryKey: ["run", runId] });
    qc.invalidateQueries({ queryKey: ["runs"] });
  };

  const cancel = useMutation({
    mutationFn: () => cancelRun(runId),
    onSuccess: (r) => {
      if (r.cancelled) {
        toast.success("Run abandoned — already-enqueued domains will drain as no-ops.");
      } else {
        toast("Run was already terminal.");
      }
      invalidateRuns();
    },
  });

  const pause = useMutation({
    mutationFn: () => pauseRun(runId),
    onSuccess: (r) => {
      if (r.paused) {
        toast.success("Run paused — workers will finish what's in flight, then wait for Resume.");
      } else {
        toast("Run wasn't in a state we could pause.");
      }
      invalidateRuns();
    },
  });

  const resume = useMutation({
    mutationFn: () => resumeRun(runId),
    onSuccess: (r) => {
      if (r.resumed) {
        toast.success("Run resumed — background producer will pick up where it left off.");
      } else {
        toast("Run wasn't paused.");
      }
      invalidateRuns();
    },
  });

  if (run.isLoading) {
    return <Page title="Run"><div className="card p-6 text-muted text-sm">Loading…</div></Page>;
  }
  if (!run.data) {
    return <Page title="Run"><div className="card p-6 text-danger text-sm">Run not found.</div></Page>;
  }

  const r = live ?? run.data;
  const isRunning = r.status === "running";
  // `producer_status` is the source of truth for the Pause/Resume button.
  // Null = legacy / CLI-streaming run; show no producer-control buttons,
  // but the Abandon button still applies as long as the run is RUNNING.
  const isPaused = r.producer_status === "paused";
  const canPause =
    isRunning &&
    (r.producer_status === "requested" || r.producer_status === "running");
  const canResume = isRunning && isPaused;

  return (
    <Page
      title={r.source}
      actions={
        <>
          <Link to="/runs" className="btn-ghost text-sm">
            <ArrowLeft className="h-4 w-4" /> All runs
          </Link>
          {canResume ? (
            <button
              className="btn-primary text-sm"
              onClick={() => resume.mutate()}
              disabled={resume.isPending}
            >
              <Play className="h-4 w-4" /> Resume
            </button>
          ) : canPause ? (
            <button
              className="btn-ghost text-sm"
              onClick={() => pause.mutate()}
              disabled={pause.isPending}
            >
              <Pause className="h-4 w-4" /> Pause
            </button>
          ) : null}
          {isRunning ? (
            <button
              className="btn-danger text-sm"
              onClick={() => {
                if (
                  confirm(
                    `Abandon run "${r.source}"?\n\n` +
                      `This is terminal — the run is marked CANCELLED and ` +
                      `cannot be resumed. Already-enqueued domains drain as ` +
                      `no-ops. Use Pause if you might want to come back to it.`,
                  )
                ) {
                  cancel.mutate();
                }
              }}
              disabled={cancel.isPending}
            >
              <Square className="h-4 w-4" /> Abandon run
            </button>
          ) : null}
        </>
      }
    >
      <PauseErrorBanner run={r} />
      <RunHero run={r} snapshots={snapshots} />

      {isRunning ? <RunTempo snapshots={snapshots} /> : null}

      <OutcomeBar run={r} />

      <section className="card p-4">
        <h2 className="text-sm font-semibold mb-2">Progress over time</h2>
        <ProgressChart runId={runId} />
      </section>

      <section className="card p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold">Domain flow</h2>
          <Link
            to="/domains"
            search={{ run_id: runId }}
            className="text-xs text-muted hover:text-accent hover:underline"
          >
            Drill into domains →
          </Link>
        </div>
        <RunSankey run={r} />
      </section>

      <RunErrors run={r} />
    </Page>
  );
}
